import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAudioPlayer } from '@/museum-app/context/audio-player-context';
import { useSettings } from '@/museum-app/context/settings-context';
import { ExhibitMediaItem } from '@/museum-app/types/models'; // Import ExhibitMediaItem
import { Box, FileText, Image as ImageIcon, Pause as PauseIcon, Play, Video, Volume2 } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import { BackButton } from '../../components/molecules/back-button'; // Import BackButton
import { ExhibitInfo } from '../../features/exhibits/components/exhibit-info';
import { useExhibits } from '../../hooks/use-exhibits';

// Dynamically import heavy media components
const LazyExhibitGallery = lazy(() =>
    import('../../features/exhibits/components/exhibit-gallery').then((module) => ({ default: module.ExhibitGallery })),
);
const LazyModelViewer = lazy(() => import('../../components/media/model/model-viewer').then((module) => ({ default: module.ModelViewer })));
const LazyMediaViewer = lazy(() => import('../../components/media/media-viewer').then((module) => ({ default: module.MediaViewer })));
// Keep LazyAudioPlayer for detailed view when active
const LazyAudioPlayer = lazy(() => import('../../components/media/audio/audio-player').then((module) => ({ default: module.AudioPlayer })));
const LazyExhibitDocuments = lazy(() =>
    import('../../features/exhibits/components/exhibit-documents').then((module) => ({ default: module.ExhibitDocuments })),
);

// Import specific skeleton components
// Removed AudioPlayerSkeleton import
import { ExhibitDocumentsSkeleton } from '../../features/exhibits/components/exhibit-documents-skeleton';
import { ExhibitGallerySkeleton } from '../../features/exhibits/components/exhibit-gallery-skeleton';
// Import the shared placeholder component
import { MediaLoadingPlaceholder } from '../../components/molecules/media-loading-placeholder';

const ExhibitDetail = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate(); // Keep navigate for error button
    const location = useLocation();
    const { t } = useTranslation();

    // Extract navigation state to see if we came from a room
    const { fromRoom, roomId } = location.state || {};

    // State for lazy loading sections (removed showAudio)
    const [showGallery, setShowGallery] = useState(false);
    const [showModel, setShowModel] = useState(false);
    const [showVideo, setShowVideo] = useState(false);
    // const [showAudio, setShowAudio] = useState(false); // Removed
    const [showDocuments, setShowDocuments] = useState(false);

    const { language } = useSettings();
    const { exhibit, isLoading, error, fetchExhibitDetail } = useExhibits();
    const { loadAudio, currentMedia, isPlaying, pause: pauseGlobalAudio } = useAudioPlayer(); // Get audio context

    console.log(exhibit);

    useEffect(() => {
        if (id) {
            fetchExhibitDetail(parseInt(id, 10));
        }
        // Reset show states when ID changes
        setShowGallery(false);
        setShowModel(false);
        setShowVideo(false);
        // setShowAudio(false); // Removed
        setShowDocuments(false);
    }, [id, fetchExhibitDetail]);

    if (isLoading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="flex items-center justify-center py-12">
                    <Skeleton className="h-12 w-12 rounded-full" />
                </div>
            </div>
        );
    }

    if (error || !exhibit) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
                    <p className="font-medium">{t('exhibits.errorLoadingTitle', 'Error Loading Exhibit')}</p>
                    <p className="mt-1 text-sm">{error?.toString() || t('exhibits.errorNotFound', 'Exhibit not found')}</p>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => navigate('/exhibits')} // Use navigate directly here
                        className="mt-2"
                    >
                        {t('common.returnToExhibitList', 'Return to Exhibit List')}
                    </Button>
                </div>
            </div>
        );
    }

    // Helper function to create media objects for components - adjusted for ExhibitTranslation structure
    const createMediaObject = (url: string, type: string, exhibitTitle: string): ExhibitMediaItem => {
        const currentTranslation = exhibit?.translations?.find((t) => t.language_code === language);
        const title = currentTranslation?.title || exhibitTitle; // Use translation title or fallback
        const description = currentTranslation?.description || null;

        return {
            id: exhibit?.id || 0,
            media_type: type,
            file_path: url.startsWith('http') ? url : '/storage/' + url, // Handle absolute vs relative URLs
            title: title,
            description: description,
            order_position: 0, // Assuming default order
            translations:
                exhibit?.translations?.map((t) => ({
                    language_code: t.language_code,
                    title: t.title, // Use title
                    description: t.description,
                    alt_text: t.title, // Use title for alt text
                    caption: t.description,
                    file_path: null, // This seems specific to translation overrides, keep null
                })) || [],
        };
    };

    // Calculate available media types based on the Exhibit model
    // Accessing media collection is complex without full type, using direct URLs for now
    const hasPhotos = exhibit?.gallery_urls && exhibit.gallery_urls.length > 0; // Check media collection if available
    const hasVideos = !!exhibit?.video_url;
    const hasAudio = !!exhibit?.audio_guide_url;
    const hasModels = !!exhibit?.model_url;
    const hasDocuments = exhibit?.media?.documents && exhibit.media.documents.length > 0; // Check media collection

    const currentTranslation = exhibit.translations.find((tr) => tr.language_code === language) || exhibit.translations[0];
    const exhibitBaseTitle = currentTranslation?.title || t('exhibits.untitledExhibit', 'Untitled Exhibit'); // Fallback title

    // Create media objects using the helper
    const audioMediaObject = hasAudio ? createMediaObject(exhibit.audio_guide_url!, 'audio', exhibitBaseTitle) : null;
    const videoMediaObject = hasVideos ? createMediaObject(exhibit.video_url!, 'video', exhibitBaseTitle) : null;
    const modelMediaObject = hasModels ? createMediaObject(exhibit.model_url!, 'model', exhibitBaseTitle) : null;
    const photoMediaItems = hasPhotos ? exhibit.gallery_urls : []; // Use items from media collection
    const documentMediaItems = hasDocuments ? exhibit.media.documents : []; // Use items from media collection

    const isCurrentAudioPlaying = currentMedia?.id === exhibit.id && currentMedia?.media_type === 'audio';

    // Determine the default tab based on available media
    const mediaTabs = [
        { key: 'gallery', condition: hasPhotos },
        { key: 'model', condition: hasModels },
        { key: 'video', condition: hasVideos },
    ];
    const defaultTab = mediaTabs.find((tab) => tab.condition)?.key || '';

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Back Button */}
            <BackButton
                to={fromRoom ? `/rooms/${roomId}` : '/exhibits'}
                text={fromRoom ? t('common.backToRoom', 'Back to Room') : t('common.backToExhibits', 'Back to Exhibits')}
                ariaLabel={fromRoom ? t('common.backToRoomAriaLabel', 'Back to room') : t('common.backToExhibitsAriaLabel', 'Back to exhibits')}
                className="mb-4" // Add some margin below the back button
            />

            {/* Title */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold">{currentTranslation?.title || t('exhibits.untitledExhibit', 'Untitled Exhibit')}</h1>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 gap-8">
                {/* Primary Column (2/3 width on medium screens and up) */}
                <div>
                    {/* Conditional Media Display: Tabs or Thumbnail */}
                    {
                        hasPhotos || hasModels || hasVideos ? (
                            // Media Tabs Section
                            <Tabs defaultValue={defaultTab} className="mb-8 w-full">
                                <TabsList className="mb-4 grid w-full grid-cols-3">
                                    {hasPhotos && (
                                        <TabsTrigger value="gallery" onClick={() => setShowGallery(true)}>
                                            <ImageIcon className="mr-2 h-4 w-4" />
                                            {t('common.gallery', 'Gallery')}
                                        </TabsTrigger>
                                    )}
                                    {hasModels && (
                                        <TabsTrigger value="model" onClick={() => setShowModel(true)}>
                                            <Box className="mr-2 h-4 w-4" />
                                            {t('common.model3d', '3D Model')}
                                        </TabsTrigger>
                                    )}
                                    {hasVideos && (
                                        <TabsTrigger value="video" onClick={() => setShowVideo(true)}>
                                            <Video className="mr-2 h-4 w-4" />
                                            {t('common.videos', 'Videos')}
                                        </TabsTrigger>
                                    )}
                                </TabsList>

                                {/* Gallery Content */}
                                {hasPhotos && (
                                    <TabsContent value="gallery">
                                        {!showGallery ? (
                                            <ExhibitGallerySkeleton onClick={() => setShowGallery(true)} />
                                        ) : (
                                            <Suspense fallback={<ExhibitGallerySkeleton />}>
                                                {/* Use photoMediaItems directly */}
                                                <LazyExhibitGallery media={photoMediaItems.map(url => createMediaObject(url, 'photo', exhibitBaseTitle))} languageCode={language} />
                                            </Suspense>
                                        )}
                                    </TabsContent>
                                )}

                                {/* 3D Model Content */}
                                {hasModels && (
                                    <TabsContent value="model">
                                        {!showModel ? (
                                            <MediaLoadingPlaceholder icon={Box}>
                                                <Button variant="secondary" size="sm" onClick={() => setShowModel(true)} className="mt-4">
                                                    <Play className="mr-2 h-4 w-4" />
                                                    {t('common.loadModel', 'Load 3D Model')}
                                                </Button>
                                            </MediaLoadingPlaceholder>
                                        ) : (
                                            <Suspense fallback={<MediaLoadingPlaceholder icon={Box} />}>
                                                <div className="h-[400px] w-full overflow-hidden rounded-lg border dark:border-neutral-800">
                                                    {modelMediaObject && (
                                                        <LazyModelViewer
                                                            media={modelMediaObject}
                                                            onLoad={() => {}}
                                                            onError={(error: any) => console.error('Error loading 3D model:', error)}
                                                        />
                                                    )}
                                                </div>
                                            </Suspense>
                                        )}
                                    </TabsContent>
                                )}

                                {/* Video Content */}
                                {hasVideos && (
                                    <TabsContent value="video">
                                        {!showVideo ? (
                                            <MediaLoadingPlaceholder icon={Video}>
                                                <Button variant="secondary" size="sm" onClick={() => setShowVideo(true)} className="mt-4">
                                                    <Play className="mr-2 h-4 w-4" />
                                                    {t('common.loadVideo', 'Load Video')}
                                                </Button>
                                            </MediaLoadingPlaceholder>
                                        ) : (
                                            <Suspense fallback={<MediaLoadingPlaceholder icon={Video} />}>
                                                {videoMediaObject && (
                                                    <LazyMediaViewer media={videoMediaObject} showControls={true} autoPlay={false} loop={false} />
                                                )}
                                            </Suspense>
                                        )}
                                    </TabsContent>
                                )}
                            </Tabs>
                        ) : exhibit.thumbnail_url ? (
                            /* Fallback Thumbnail Image - Use exhibitBaseTitle */
                            <div className="mb-8">
                                <img
                                    src={exhibit.thumbnail_url}
                                    alt={exhibitBaseTitle}
                                    className="h-auto max-h-[400px] w-full rounded-lg border object-contain dark:border-neutral-800"
                                />
                            </div>
                        ) : null /* Or a placeholder if desired */
                    }

                    {hasAudio && (
                        <div className="mb-8">
                            <h2 className="mb-4 flex items-center text-xl font-semibold">
                                <Volume2 className="mr-2 h-5 w-5" />
                                {t('common.audioGuide', 'Audio Guide')}
                            </h2>
                            {audioMediaObject && (
                                <div className="space-y-4">
                                    <Button
                                        variant={isCurrentAudioPlaying ? 'outline' : 'default'}
                                        onClick={() => {
                                            if (isCurrentAudioPlaying && isPlaying) {
                                                pauseGlobalAudio();
                                            } else {
                                                loadAudio(audioMediaObject, true);
                                            }
                                        }}
                                        className="w-full"
                                    >
                                        {isCurrentAudioPlaying && isPlaying ? (
                                            <PauseIcon className="mr-2 h-4 w-4" />
                                        ) : (
                                            <Play className="mr-2 h-4 w-4" />
                                        )}
                                        {isCurrentAudioPlaying && isPlaying
                                            ? t('common.pauseAudio', 'Pause Audio')
                                            : t('common.playAudio', 'Play Audio Guide')}
                                    </Button>

                                    {/* Conditionally render the detailed player if this audio is active */}
                                    {/* {isCurrentAudioPlaying && (
                                        <Suspense fallback={<Skeleton className="h-48 w-full rounded-lg" />}>
                                            <LazyAudioPlayer
                                                media={audioMediaObject}
                                                showTranscript={true}
                                                className="mt-4"
                                            />
                                        </Suspense>
                                    )} */}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Exhibit Information (Always shown) */}
                    <div className="mb-8">
                        <h2 className="mb-4 text-xl font-semibold">{t('common.information', 'Information')}</h2>
                        <ExhibitInfo exhibit={exhibit} languageCode={language} />
                    </div>
                </div>

                {/* Secondary Column (1/3 width on medium screens and up) */}
                <div>
                    {/* Audio Guides Section */}

                    {/* Documents Section */}
                    {hasDocuments && (
                        <div className="mb-8">
                            <h2 className="mb-4 flex items-center text-xl font-semibold">
                                <FileText className="mr-2 h-5 w-5" />
                                {t('common.documents', 'Documents')}
                            </h2>
                            {!showDocuments ? (
                                <ExhibitDocumentsSkeleton onClick={() => setShowDocuments(true)} />
                            ) : (
                                <Suspense fallback={<ExhibitDocumentsSkeleton />}>
                                    {/* Use documentMediaItems directly */}
                                    <LazyExhibitDocuments documents={documentMediaItems} currentLanguage={language} />
                                </Suspense>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ExhibitDetail;
